import React from "react";
import { ArrowUp, Phone, MapPin, Navigation, Download } from "lucide-react";
import { BoatLogo } from "./BoatLogo";
import { useLanguage } from "../context/LanguageContext";
import { PHONE, PHONE_RAW, MAPS_DIRECTIONS_URL } from "../lib/data";

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-slate-900 text-slate-300"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 md:py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-500 grid place-items-center text-slate-900 shadow-lg rotate-[-4deg]">
                <BoatLogo className="w-7 h-7" strokeColor="#0F172A" />
              </span>
              <span className="font-heading text-xl font-semibold tracking-tight text-white">
                Pasohlávky Motorové čluny
              </span>
            </div>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
              {t.footer.tagline} ATC Merkur, Pasohlávky.
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-white font-heading text-sm tracking-widest uppercase">
              {t.nav.contact}
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${PHONE_RAW}`}
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-2 hover:text-yellow-300 transition-colors"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.5} />
                  {PHONE}
                </a>
              </li>
              <li className="inline-flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5" strokeWidth={2.5} />
                <span className="leading-relaxed">
                  Pasohlávky 61, 691 22
                  <br />
                  ATC Merkur
                </span>
              </li>
              <li>
                <a
                  href={MAPS_DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-directions"
                  className="inline-flex items-center gap-2 hover:text-yellow-300 transition-colors"
                >
                  <Navigation className="w-4 h-4" strokeWidth={2.5} />
                  {t.nav.directions}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-white font-heading text-sm tracking-widest uppercase">
              {t.contact.hoursTitle}
            </h4>
            <ul className="mt-4 space-y-2 text-sm text-slate-400">
              <li>
                <span className="text-slate-200">
                  {t.contact.hours.spring.range}
                </span>{" "}
                — {t.contact.hours.spring.value}
              </li>
              <li>
                <span className="text-slate-200">
                  {t.contact.hours.summer.range}
                </span>{" "}
                — {t.contact.hours.summer.value}
              </li>
              <li>
                <span className="text-slate-200">
                  {t.contact.hours.autumn.range}
                </span>{" "}
                — {t.contact.hours.autumn.value}
              </li>
            </ul>
          </div>
        </div>

        {/* Download static site */}
        <div
          data-testid="download-banner"
          className="mt-12 rounded-3xl bg-gradient-to-br from-sky-500/15 to-yellow-400/10 border border-white/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
        >
          <div className="w-14 h-14 shrink-0 grid place-items-center rounded-2xl bg-yellow-400 text-slate-900 shadow-[0_10px_30px_rgba(250,204,21,0.4)]">
            <Download className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <h4 className="font-heading text-white text-lg md:text-xl font-semibold tracking-tight">
              {t.footer.downloadTitle}
            </h4>
            <p className="mt-1.5 text-sm text-slate-400 leading-relaxed max-w-xl">
              {t.footer.downloadDesc}
            </p>
          </div>
          <a
            href="/pasohlavky-web.zip"
            download="pasohlavky-motorovecluny.zip"
            data-testid="download-zip-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-6 py-3.5 text-sm font-semibold shadow-[0_10px_28px_rgba(250,204,21,0.4)] hover:-translate-y-0.5 transition-all whitespace-nowrap"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            {t.footer.downloadCta}
          </a>
        </div>

        <div className="mt-10 pt-7 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-slate-500">
          <div>
            © {year} Pasohlávky Motorové čluny. {t.footer.rights}
          </div>
          <button
            data-testid="back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 self-start md:self-auto rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-4 py-2 transition"
          >
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={2.5} />
            {t.footer.backTop}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
